import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import { parseThemeApiResponse, siteThemeAdminUrl } from "../ThemeManagement/themeApiUtils";
import "../../CSS/Global.css";
import "./AppearanceCustomize.css";

const AppearanceCustomize = () => {
  const urlmain = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const [payload, setPayload] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setPayload(res.data);
        setSettings(res.data?.settings || {});
      } else {
        toast.error(res.message || "No active theme");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings"), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ settings }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success("Settings saved");
        setPayload(res.data);
        setSettings(res.data?.settings || {});
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("setting_key", key);
    body.append("file", file);
    try {
      const response = await fetch(siteThemeAdminUrl("/settings/upload"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        handleChange(key, res.data.url);
        toast.success("Image uploaded");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const assetUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${urlmain}${path}`;
  };

  const renderField = (field) => {
    const value = settings[field.key];
    const fullWidth = field.type === "image" || field.type === "text";

    if (field.type === "toggle") {
      return (
        <label key={field.key} className={`appearance-customize__toggle ${fullWidth ? "appearance-customize__field--full" : ""}`}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleChange(field.key, e.target.checked)}
          />
          {field.label}
        </label>
      );
    }

    if (field.type === "color") {
      const colorValue = value || field.default || "#000000";
      return (
        <div key={field.key} className={`appearance-customize__field ${fullWidth ? "appearance-customize__field--full" : ""}`}>
          <span className="appearance-customize__label">{field.label}</span>
          <div className="appearance-customize__color-row">
            <input
              type="color"
              className="appearance-customize__color-input"
              value={colorValue}
              onChange={(e) => handleChange(field.key, e.target.value)}
            />
            <span className="appearance-customize__color-value">{colorValue}</span>
          </div>
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div key={field.key} className="appearance-customize__field">
          <label className="appearance-customize__label" htmlFor={field.key}>{field.label}</label>
          <select
            id={field.key}
            className="appearance-customize__select"
            value={value || field.default || ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === "text") {
      return (
        <div key={field.key} className="appearance-customize__field appearance-customize__field--full">
          <label className="appearance-customize__label" htmlFor={field.key}>{field.label}</label>
          <input
            id={field.key}
            type="text"
            className="appearance-customize__input"
            value={value ?? field.default ?? ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
          />
        </div>
      );
    }

    if (field.type === "image") {
      return (
        <div key={field.key} className="appearance-customize__field appearance-customize__field--full">
          <span className="appearance-customize__label">{field.label}</span>
          {value && (
            <img
              src={assetUrl(value)}
              alt={field.label}
              className="appearance-customize__preview"
            />
          )}
          <input
            type="file"
            accept="image/*"
            className="appearance-customize__file"
            onChange={(e) => handleImageUpload(field.key, e.target.files?.[0])}
          />
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <p className="appearance-customize__loading">Loading customize settings...</p>;
  }

  if (!payload) {
    return (
      <div className="appearance-customize">
        <h1 className="dashboard-title">Customize</h1>
        <p className="appearance-customize__empty">No active theme. Install and activate a theme first.</p>
      </div>
    );
  }

  return (
    <div className="appearance-customize">
      <div className="appearance-customize__header oms-header">
        <div>
          <h1 className="dashboard-title">Customize — {payload.theme?.name}</h1>
          <p className="appearance-customize__subtitle">
            Tune colors, images, and options for your storefront theme.
          </p>
        </div>
        <button className="oms-add-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="appearance-customize__groups">
        {(payload.configSchema?.groups || []).map((group) => (
          <section key={group.key} className="appearance-customize__group">
            <h3 className="appearance-customize__group-title">{group.label}</h3>
            {group.description && (
              <p className="appearance-customize__group-desc">{group.description}</p>
            )}
            <div className="appearance-customize__fields">
              {(group.fields || []).map((field) => renderField(field))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default AppearanceCustomize;
